import dayjs from "@/lib/dayjs.config";
import { API_DATE_TIME_FORMAT, DATE_FORMAT } from "../constants";

export const formatDate = (
  date: string | Date,
  format = DATE_FORMAT.dateTime,
): string => {
  return dayjs(date).format(format);
};

export const toUTC = (
  date: string | Date,
  format = DATE_FORMAT.dateTime,
): string => {
  return dayjs(date).utc().format(format);
};

export const fromUTC = (date: string): dayjs.Dayjs => {
  return dayjs.utc(date).tz();
};

export const setSystemTimezone = (timezone: string): void => {
  dayjs.tz.setDefault(timezone);
};

export const convertToTimeZone = (
  date: string | Date,
  currentTimeZone: string,
  targetTimeZone: string,
  format = DATE_FORMAT.dateTime,
): string => {
  return dayjs.tz(date, currentTimeZone).tz(targetTimeZone).format(format);
};

export const normalizeToMidnight = (
  date: Date,
  setHours: boolean = true,
): Date => {
  const d = dayjs(date);
  return setHours ? d.startOf("day").toDate() : d.toDate();
};

export const formatToApiDateTime = (date: Date): string => {
  return dayjs(date).format(API_DATE_TIME_FORMAT);
};

export const parseDateValue = (
  date: string | Date | undefined,
  formats: string[] = [
    API_DATE_TIME_FORMAT,
    DATE_FORMAT.dateTime,
    DATE_FORMAT.date,
  ],
): Date | undefined => {
  if (!date) {
    return undefined;
  }

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  for (const format of formats) {
    const parsed = dayjs(date, format, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
  }

  const fallback = dayjs(date);
  return fallback.isValid() ? fallback.toDate() : undefined;
};

export const parseUTCDateValue = (
  date: string | Date | undefined,
  formats: string[] = [
    API_DATE_TIME_FORMAT,
    DATE_FORMAT.dateTime,
    DATE_FORMAT.date,
  ],
): Date | undefined => {
  if (!date) {
    return undefined;
  }

  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  for (const format of formats) {
    const parsed = dayjs.utc(date, format, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
  }

  const fallback = dayjs.utc(date);
  return fallback.isValid() ? fallback.toDate() : undefined;
};

export const normalizeDashboardPresetDate = (
  date: string | null | undefined,
): string | undefined => {
  if (!date) {
    return undefined;
  }

  const apiDate = dayjs(date, API_DATE_TIME_FORMAT, true);
  if (apiDate.isValid()) {
    return date;
  }

  const parsedDate = parseUTCDateValue(date);
  return parsedDate
    ? dayjs.utc(parsedDate).format(API_DATE_TIME_FORMAT)
    : undefined;
};

export const getTimeDiff = (
  start: string | Date,
  end: string | Date,
): string => {
  const startTime = dayjs(start);
  const endTime = dayjs(end);

  const diffInSeconds = endTime.diff(startTime, "second");

  const hours = String(Math.floor(diffInSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((diffInSeconds % 3600) / 60)).padStart(
    2,
    "0",
  );
  const seconds = String(diffInSeconds % 60).padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};

export const formatDateToTZ = (
  date: string | Date,
  format = DATE_FORMAT.dateTime,
): string => {
  return dayjs.utc(date).format(format);
};

export const getDateRange = (
  range: string,
  format?: string,
  timeZone?: string,
): { fromDate: string | Date; toDate: string | Date } => {
  let now = dayjs();
  if (timeZone === "") {
    now = now.utc();
  } else if (timeZone) {
    now = now.tz(timeZone);
  }

  let fromDate: dayjs.Dayjs | string = now;
  let toDate: dayjs.Dayjs | string = now;

  switch (range) {
    case "Today":
      fromDate = now.startOf("day");
      toDate = now;
      break;
    case "Yesterday":
      fromDate = now.subtract(1, "day").startOf("day");
      toDate = now.startOf("day");
      break;
    case "Last Week":
      fromDate = now.subtract(1, "week").startOf("day");
      toDate = now.startOf("day");
      break;
    case "Last 7 Days":
      fromDate = now.subtract(6, "day").startOf("day");
      toDate = now;
      break;
    case "Last Month":
      fromDate = now.subtract(1, "month").startOf("month");
      toDate = now.startOf("month");
      break;
    case "Last 30 Days":
      fromDate = now.subtract(30, "day").startOf("day");
      toDate = now;
      break;
    case "Last 3 Months":
      fromDate = now.startOf("month").subtract(2, "month");
      toDate = now.startOf("day");
      break;
    case "Current Month":
      fromDate = now.startOf("month");
      toDate = now;
      break;
    case "Current Year":
      fromDate = now.startOf("year");
      toDate = now;
      break;
    case "Last Year":
      fromDate = now.subtract(1, "year").startOf("year");
      toDate = now.startOf("year");
      break;
    case "Custom":
      fromDate = "";
      toDate = "";
      break;
    default:
      fromDate = now.startOf("day");
      toDate = now;
      break;
  }

  if (format) {
    return {
      fromDate: dayjs.isDayjs(fromDate) ? fromDate.format(format) : fromDate,
      toDate: dayjs.isDayjs(toDate) ? toDate.format(format) : toDate,
    };
  }

  return {
    fromDate: dayjs.isDayjs(fromDate) ? fromDate.toDate() : fromDate,
    toDate: dayjs.isDayjs(toDate) ? toDate.toDate() : toDate,
  };
};

export const getDateRangeError = (
  fromDate: string | Date | undefined,
  toDate: string | Date | undefined,
  type: "from" | "to",
): string | undefined => {
  if (!fromDate || !toDate) {
    return undefined;
  }

  const start = dayjs(fromDate);
  const end = dayjs(toDate);

  if (!start.isValid() || !end.isValid()) {
    return "Invalid date format";
  }

  if (start.isSame(end)) {
    return type === "from"
      ? "From Date cannot be same as To Date"
      : "To Date cannot be same as From Date";
  }

  if (type === "from" && start.isAfter(end)) {
    return "From Date cannot be after To Date";
  }

  if (type === "to" && end.isBefore(start)) {
    return "To Date cannot be before From Date";
  }

  return undefined;
};

export const roundDownToInterval = (
  date: string | Date | dayjs.Dayjs,
  intervalMinutes: number,
): dayjs.Dayjs => {
  const d = dayjs(date);
  const minutes = d.minute();
  const roundedMinutes =
    Math.floor(minutes / intervalMinutes) * intervalMinutes;
  return d.minute(roundedMinutes).second(0).millisecond(0);
};

export const roundUpToInterval = (
  date: string | Date | dayjs.Dayjs,
  intervalMinutes: number,
): dayjs.Dayjs => {
  const d = dayjs(date);
  const minutes = d.minute();
  const seconds = d.second();
  const milliseconds = d.millisecond();

  if (minutes % intervalMinutes === 0 && seconds === 0 && milliseconds === 0) {
    return d;
  }

  const roundedMinutes =
    Math.ceil(
      (minutes + seconds / 60 + milliseconds / 60000) / intervalMinutes,
    ) * intervalMinutes;
  return d.minute(roundedMinutes).second(0).millisecond(0);
};

export const formatDateTimeWithOffset = (
  date: string | Date,
  format = DATE_FORMAT.dateTime,
): string => {
  const withoutOffset = String(date).replace(/([+-]\d{2}:\d{2}|Z)$/, "");
  return dayjs(withoutOffset).format(format);
};

export const getUserCurrentDateTime = (timezone: string): Date => {
  const now = dayjs().tz(timezone);

  return new Date(
    now.year(),
    now.month(),
    now.date(),
    now.hour(),
    now.minute(),
    now.second(),
    now.millisecond(),
  );
};
